var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Popup_1;
import ComponentRegistry from '@slickdeals/durango/dist/javascript/core/componentRegistry';
import { defaultOptions } from '@slickdeals/durango/dist/javascript/decorators/defaultOptions';
import { injectable } from '@slickdeals/durango/dist/javascript/decorators/injectable';
import { stateable } from '@slickdeals/durango/dist/javascript/decorators/stateable';
import { showable, MediatedEvents as ShowableMediatedEvents } from '@slickdeals/durango/dist/javascript/decorators/showable';
import { defer } from '@slickdeals/durango/dist/javascript/utilities/defer';
import { isInDom } from '@slickdeals/durango/dist/javascript/utilities/dom';
import { isToggleable } from '@slickdeals/durango/dist/javascript/utilities/guards';
import { positionElements, Positions } from '@slickdeals/durango/dist/javascript/utilities/positioning';
import BlueprintComponent, { ClassNames as BlueprintComponentClassNames } from '../core/blueprintComponent';
import { mediatable } from '../decorators/mediatable';
import { name as overlayName } from './overlay';
import '../../scss/components/popup.scss';
/** These are the anchor types for the popup */
export var AnchorTypes;
(function (AnchorTypes) {
    AnchorTypes["Trigger"] = "trigger";
    AnchorTypes["Body"] = "body";
})(AnchorTypes || (AnchorTypes = {}));
/** The name of the module */
export const name = 'Popup';
/** Container ID for body parented popups */
const bodyPopupContainerId = `Popup-BlueprintWrapper-${Math.floor(Math.random() * Math.floor(9999))}`;
export var TriggerEvents;
(function (TriggerEvents) {
    TriggerEvents["Click"] = "click";
    TriggerEvents["Hover"] = "mouseover";
})(TriggerEvents || (TriggerEvents = {}));
/** Class names for Popups */
export var ClassNames;
(function (ClassNames) {
    ClassNames["Default"] = "bp-c-popup";
    ClassNames["JavaScript"] = "js-popup";
    ClassNames["Content"] = "bp-c-popup_content";
    ClassNames["JavaScriptContent"] = "js-popup_content";
})(ClassNames || (ClassNames = {}));
// Type guard for checking if a variable contains valid Popup content
const isContent = (value) => (typeof value === 'string' || value instanceof HTMLElement || value instanceof Text || value instanceof DocumentFragment);
let Popup = Popup_1 = class Popup extends BlueprintComponent {
    /**
     * Create a Popup UI component
     *
     * @param options Data and options to initialize the component with
     */
    constructor(options) {
        super(name, options);
        // Set the initial state
        const initState = {
            visible: false,
        };
        // Alias event handler for de-registering event handlers later
        this.handleTrigger = this.handleTrigger.bind(this);
        // Initialize the module's state
        this.initState(initState);
    }
    /**
     * Performs any necessary initialization for the component
     */
    async init() {
        super.init();
        // If the element wasn't already created by a module extending this, then initialize it
        if (!this.element) {
            await this.initElement();
        }
        // If the element exists, then make sure it's in the DOM and initialize the trigger element
        if (this.element) {
            if (this.options.anchorType === AnchorTypes.Trigger && (this.options.triggerId || this.options.triggerElement)) {
                this.initTriggerElement(!isInDom(this.element));
            }
            else if (!isInDom(this.element)) {
                document.body.appendChild(this.element);
            }
        }
        if (this.options.changeParentToBody) {
            this.handleBodyInjection();
        }
    }
    /**
     * Handles moving the voting thumbs button popup to the body
     */
    handleBodyInjection() {
        let popupContainer = document.getElementById(bodyPopupContainerId);
        if (!popupContainer) {
            popupContainer = document.createElement('div');
            popupContainer.setAttribute('id', bodyPopupContainerId);
            popupContainer.classList.add(BlueprintComponentClassNames.Wrapper);
            document.body.appendChild(popupContainer);
        }
        popupContainer.appendChild(this.element);
    }
    /**
     * Perform any required cleanup to properly destroy the component
     *
     * @param cleanDom Should dynamically-inserted DOM elements be removed?
     */
    destroy(cleanDom = false) {
        super.destroy();
        this.triggerElement.removeEventListener(this.options.triggerEvent, this.handleTrigger);
        if (cleanDom) {
            this.element.parentNode.removeChild(this.element);
        }
    }
    /**
     * Loads the element from the DOM or creates the element dynamically
     */
    async initElement() {
        if (this.options.id) {
            // If an ID exists, then load the element from the DOM
            this.element = await BlueprintComponent.loadElement(name, this.options);
        }
        else if (isContent(this.options.content)) {
            // Otherwise create the element
            this.element = Popup_1.createElement({
                content: this.options.content,
            });
        }
    }
    /**
     * Load and initialize the trigger element
     */
    async initTriggerElement(appendElement = false) {
        if (this.options.triggerElement && this.options.triggerElement instanceof HTMLElement) {
            // Load the trigger element from the options object
            this.triggerElement = this.options.triggerElement;
        }
        else {
            // Load the trigger element from the DOM
            this.triggerElement = document.getElementById(this.options.triggerId);
        }
        // Add event listener for clicks on the trigger element
        if (this.options.triggerEvent === TriggerEvents.Click) {
            this.triggerElement.addEventListener(this.options.triggerEvent, this.handleTrigger);
        }
        else {
            this.initHoverEventListeners();
        }
        // Append the element to the trigger element if needed
        if (appendElement) {
            this.triggerElement.appendChild(this.element);
        }
    }
    /**
     * Handle a click on the trigger element
     */
    handleTrigger() {
        // Show the Popup if it's currently hidden
        if (!this.isVisible()) {
            if (this.options.anchorType === AnchorTypes.Trigger) {
                // Position the element before displaying it
                positionElements({
                    target: this.triggerElement,
                    orbiter: this.element,
                    position: this.options.position,
                });
            }
            this.show(this.options.overlay);
        }
    }
    /**
     * Prehide hook from Showable
     */
    prehide() {
        // Toggle the trigger to "off" state when Popup is hiding
        this.toggleTrigger(false);
    }
    /**
     * Handles toggling of the trigger element if it's a toggleable item
     *
     * @param toggled If the trigger should be in an "on" or "off" state
     */
    toggleTrigger(toggled) {
        // Get all registered components for the trigger HTML element
        const components = ComponentRegistry.get(this.triggerElement);
        if (components) {
            // Loop through all the registered components for the trigger and find any toggleable components
            Object.values(components).forEach(component => {
                if (isToggleable(component)) {
                    // Toggle the trigger element
                    component.toggle(toggled);
                }
            });
        }
    }
    /**
     * Adds deferred event listeners to triggerElement and popup
     * to handle the showing and hiding of the popup to be "smart"
     *
     * "smart" being moving between the triggerElement and popup and having
     * the popup remain open. Also moving off of the trigger or popup and returning
     * within the delay and having the popup remain open.
     */
    initHoverEventListeners() {
        const elements = [this.triggerElement, this.element];
        elements.forEach(element => {
            let enterDeferrable;
            let exitDeferrable;
            element.addEventListener('mouseenter', () => {
                if (typeof exitDeferrable === 'function') {
                    exitDeferrable();
                    exitDeferrable = null;
                }
                enterDeferrable = defer(this.handleTrigger, this.options.delay);
            });
            element.addEventListener('mouseleave', () => {
                if (typeof enterDeferrable === 'function') {
                    enterDeferrable();
                    enterDeferrable = null;
                }
                exitDeferrable = defer(this.hide.bind(this), this.options.delay);
            });
        });
    }
    /**
     * Builds and returns the content element for the Popup
     *
     * @param content Content to be injected into the Popup
     */
    static getContentContainer(content) {
        // Create the HTML element to build on
        const contentElement = document.createElement('div');
        // Add Content class names
        contentElement.classList.add(ClassNames.Content, ClassNames.JavaScriptContent);
        // Determine content type and inject it into the content container
        if (typeof content === 'string') {
            contentElement.appendChild(document.createTextNode(content));
        }
        else {
            contentElement.appendChild(content);
        }
        return contentElement;
    }
    /**
     * Create a Popup HTML element
     *
     * @param options Options used for creating the element
     */
    static createElement(options) {
        // Create the HTML element to build on
        const element = document.createElement('div');
        // Add Popup class names
        element.classList.add(ClassNames.Default, ClassNames.JavaScript, 'blueprint');
        // Add ID to the element
        if (options.id) {
            element.id = options.id;
        }
        // Add content to the element
        element.appendChild(Popup_1.getContentContainer(options.content));
        return element;
    }
};
Popup = Popup_1 = __decorate([
    showable(overlayName),
    stateable(name),
    mediatable(name, Object.values(ShowableMediatedEvents)),
    injectable(name),
    defaultOptions({
        triggerEvent: TriggerEvents.Click,
        position: [Positions.Bottom, Positions.Left],
        anchorType: AnchorTypes.Trigger,
        delay: 250,
        changeParentToBody: false,
    })
], Popup);
// Create any Popup components in the hydration queue
BlueprintComponent.instantiate(name);
export default Popup;
